import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild, TemplateRef, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter, take, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { HealthPlan } from '../../../../core/models/procedure.model';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { TranslateModule } from '@ngx-translate/core';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';

import { AddHealthPlanModalComponent } from '../add-health-plan-modal/add-health-plan-modal';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { HealthPlanProceduresModalComponent } from '../health-plan-procedures-modal/health-plan-procedures-modal.component';

@Component({
    selector: 'app-insurance-procedures',
    standalone: true,
    imports: [
        CommonModule, 
        AddHealthPlanModalComponent, 
        TranslateModule, 
        AppPrimeTable, 
        AppButton, 
        IconFieldModule, 
        InputIconModule, 
        InputTextModule, 
        FormsModule,
        AppDropdown,
        HasPermissionDirective,
        HealthPlanProceduresModalComponent
    ],
    templateUrl: './insurance-procedures.html',
})
export class InsuranceProcedures implements OnInit, OnChanges, OnDestroy {
    @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
    @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

    /** Set via ?tab=insurance&editEntityId=<id> (opened in a new tab from the
     *  "Registar Faturação" modal's "Edit insurance details" link) — auto-opens
     *  that entity's edit modal once loaded. */
    @Input() editEntityId: number | null = null;
    
    templates: { [key: string]: TemplateRef<any> } = {};

    healthPlans: HealthPlan[] = [];
    loading = true;

    // Table Configuration
    private allColumns: PrimeTableColumn[] = [
        { field: 'name', header: 'patient_field_name' },
        { field: 'cell_phone', header: 'patient_field_mobile' },
        { field: 'email', header: 'patient_field_email' },
        { field: 'procedure_count', header: 'procedures_label' },
        { field: 'active', header: 'clinic_procedures_table_status', type: 'boolean' },
        { field: 'actions', header: 'clinic_procedures_table_actions', type: 'action', width: '100px', styleClass: 'text-center' }
    ];

    // Hide the actions column entirely for view-only users (its items require edit access)
    get columns(): PrimeTableColumn[] {
        if (this.perm.canEdit(MODULE.PROCEDIMENTOS)) return this.allColumns;
        return this.allColumns.filter((c) => c.field !== 'actions');
    }

    totalRecords = 0;
    rows = 10;
    first = 0;
    searchText = '';
    branchId: number | null = null;
    openMenuId: number | null = null;

    private searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;

    // Modal states
    showAddModal = false;
    selectedHealthPlan: any | null = null; // For editing
    showProceduresModal = false;
    selectedHealthPlanForProcedures: any | null = null;
    private lazyLoadTriggered = false;

    private perm = inject(PermissionService);

    constructor(
        private proceduresService: ProceduresService,
        private store: Store,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.templates = {
            'active': this.statusTemplate,
            'actions': this.actionsTemplate
        };

        this.store.select(selectUserInfo).pipe(
            filter(info => !!info),
            take(1)
        ).subscribe(userInfo => {
            this.branchId = userInfo.branches_id || userInfo.branchId;
            if (this.branchId) {
                if (this.lazyLoadTriggered) {
                    this.loadHealthPlans();
                }
            } else {
                this.loading = false;
            }
        });

        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(text => {
            this.searchText = text;
            this.first = 0;
            this.loadHealthPlans();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['editEntityId'] && this.editEntityId) {
            this.openEditModalForEntity(this.editEntityId);
        }
    }

    ngOnDestroy() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        this.searchSubject.complete();
    }

    private async openEditModalForEntity(id: number) {
        const { data, error } = await this.proceduresService.getHealthPlanById(id);
        if (error || !data) {
            console.error('Failed to load insurer for editing', error);
            return;
        }
        this.selectedHealthPlan = data;
        this.showAddModal = true;
        this.cdr.detectChanges();
    }

    async loadHealthPlans() {
        if (!this.branchId) return;

        this.loading = true;

        try {
            const { data, error } = await this.proceduresService.getHealthPlans(
                this.branchId,
                this.rows,
                this.first,
                this.searchText
            );

            if (error) {
                this.healthPlans = [];
                this.totalRecords = 0;
            } else if (data) {
                const res = data as any;
                this.healthPlans = res.health_plans || [];
                
                if (res.total_records !== undefined) {
                    this.totalRecords = res.total_records;
                } else if (res.total_pages) {
                    this.totalRecords = res.total_pages * this.rows;
                } else {
                    this.totalRecords = this.healthPlans.length;
                }
            } else {
                this.healthPlans = [];
                this.totalRecords = 0;
            }

        } catch (error) {
            this.healthPlans = [];
            this.totalRecords = 0;
        } finally {
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    onSearch(text: string) {
        this.searchSubject.next(text);
    }

    onPageChange(event: any) {
        this.lazyLoadTriggered = true;
        this.first = event.first;
        this.rows = event.rows;
        this.loadHealthPlans();
    }

    toggleMenu(id: number, event: Event) {
        event.stopPropagation();
        if (this.openMenuId === id) {
            this.openMenuId = null;
        } else {
            this.openMenuId = id;
        }
    }

    openAddModal() {
        this.selectedHealthPlan = null;
        this.showAddModal = true;
    }

    onAddHealthPlanSuccess() {
        this.showAddModal = false;
        this.selectedHealthPlan = null;
        this.loadHealthPlans();
        // Show success toast?
    }

    onModalClose() {
        this.showAddModal = false;
        this.selectedHealthPlan = null;
    }

    async toggleStatus(plan: HealthPlan) {
        this.openMenuId = null;
        if (!this.perm.canEdit(MODULE.PROCEDIMENTOS)) return; // status change requires edit access
        if (!plan.id) return;

        // Optimistic update or wait?
        // Let's wait.
        try {
            const newStatus = !plan.active;
            const { error } = await this.proceduresService.updateHealthPlan(plan.id, { active: newStatus });

            if (!error) {
                plan.active = newStatus;
                this.cdr.detectChanges();
            } else {
                console.error('Error toggling status:', error);
            }
        } catch (e) {
            console.error('Exception toggling status:', e);
        }
    }

    editHealthPlan(plan: HealthPlan) {
        this.openMenuId = null;
        if (!plan.id) return;
        // The table rows come from filter_health_plans_with_procedure_count, which only
        // returns a subset of the columns (no nipc/address/city/...). Re-fetch the full
        // row so the edit modal is populated with everything that is stored.
        this.openEditModalForEntity(plan.id);
    }

    viewProcedures(plan: HealthPlan) {
        this.selectedHealthPlanForProcedures = plan;
        this.showProceduresModal = true;
    }

    closeProceduresModal() {
        this.showProceduresModal = false;
        this.selectedHealthPlanForProcedures = null;
    }
}
